import { useEffect, useState } from "react";

import { Classes, Icon } from "@blueprintjs/core";
import { useLocation } from "react-router";
import { toast } from "sonner";

import { Box, Flex } from "@/components/layout/flexbox";
import { useAuth } from "@/components/lib/auth";
import { CalendarSettingDialog } from "@/components/pieces/coaching/CoachingSettings/CalendarAccountSetting/CalendarSettingDialog";
import { GrantCalendarAccess } from "@/components/pieces/google_login/GrantCalendarAccess";
import { StyledButton } from "@/components/pieces/link/LinkButton";
import { GrantOutlookCalendarAccess } from "@/components/pieces/outlook-login/GrantOutlookCalendarAccess";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import {
  CalendarAccountsDocument,
  useAuthCalendarAccountMutation,
  useCalendarAccountsQuery,
  useMakePrimaryCalendarAccountMutation,
  useRemoveCalendarAccountMutation,
} from "@/graphql";
import { CalendarAccount } from "@/types/calendars";
import { ensureNonNullArray } from "@/utils/array";

import { CalendarAccountBlock } from "./CalendarAccountsBlock";

interface CalendarAccountSectionProps {
  calendarAccounts?: CalendarAccount[];
  isPrimary: boolean;
  hasPrimary: boolean;
  onSettingChanged: () => void;
}

function CalendarAccountSection({
  calendarAccounts,
  isPrimary,
  hasPrimary,
  onSettingChanged,
}: CalendarAccountSectionProps) {
  const [selectedCalendarAccount, setSelectedCalendarAccount] =
    useState<CalendarAccount | null>(null);
  const [addingNew, setAddingNew] = useState(
    isPrimary && !calendarAccounts?.length,
  );

  const [calendarSettingDialogVisible, setCalendarSettingDialogVisible] =
    useState(false);

  const location = useLocation();
  const { currentWorkspace } = useAuth();

  useEffect(() => {
    const outlookAuthorizeSuccess = location.state?.outlookAuthorizeSuccess;
    if (outlookAuthorizeSuccess === true) {
      toast.success("Saved");
      setAddingNew(false);
      onSettingChanged();
    } else if (outlookAuthorizeSuccess === false) {
      const outlookAuthorizeError =
        location.state.outlookAuthorizeError || "Something went wrong";
      toast.error(outlookAuthorizeError);
    }
  }, []);

  const onOpenCalendarSettingModal = (account: CalendarAccount) => {
    setSelectedCalendarAccount(account);
    setCalendarSettingDialogVisible(true);
  };

  useEffect(() => {
    if (isPrimary && !hasPrimary && !addingNew) {
      setAddingNew(true);
    }
  }, [addingNew]);

  const [authAccount, { loading }] = useAuthCalendarAccountMutation({
    onError: () => {
      toast.error("Something went wrong");
    },
    onCompleted: (response) => {
      const calendarAccount =
        response.authCalendarAccount?.calendarAccount || null;
      if (calendarAccount) {
        onOpenCalendarSettingModal(calendarAccount);
      }
      toast.success("Saved");
      setAddingNew(false);
      onSettingChanged();
    },
    refetchQueries: [{ query: CalendarAccountsDocument }],
    awaitRefetchQueries: true,
  });

  const [removeAccount, { loading: removing }] =
    useRemoveCalendarAccountMutation({
      onError: () => {
        toast.error("Something went wrong");
      },
      onCompleted: (response) => {
        if (!response.removeCalendarAccount) return;

        const {
          removeCalendarAccount: { error },
        } = response;
        if (error && error.message) {
          toast.error(error.message);
        } else {
          toast.success("Saved");
          onSettingChanged();
        }
      },
      refetchQueries: [{ query: CalendarAccountsDocument }],
      awaitRefetchQueries: true,
    });

  const [makePrimary, { loading: makingPrimary }] =
    useMakePrimaryCalendarAccountMutation({
      onError: () => {
        toast.error("Something went wrong");
      },
      onCompleted: (response) => {
        if (!response || !response.makePrimaryCalendarAccount) {
          return;
        }
        const {
          makePrimaryCalendarAccount: { error },
        } = response;
        if (error && error.message) {
          toast.error(error.message);
        } else {
          toast.success("Saved");
          onSettingChanged();
        }
      },
      awaitRefetchQueries: true,
      refetchQueries: [{ query: CalendarAccountsDocument }],
    });
  const updating = removing || makingPrimary;

  const responseGoogleHandler = ({ code }: { code: string }) => {
    if (code) {
      authAccount({
        variables: {
          authCode: code,
          redirectUri: window.location.origin,
          isPrimary,
          accountType: "GOOGLE",
        },
      });
    }
  };

  return (
    <>
      <Flex justifyContent="space-between" flexWrap="wrap">
        <Box flexBasis={["100%", "50%"]}>
          <Box fontWeight={500}>
            {isPrimary ? "Primary Calendar" : "Check for Schedule Conflicts"}
          </Box>
          <Box mt={2} color={SUBTEXT_COLOR} maxWidth={["100%", 380]}>
            {isPrimary
              ? "Calendar events get added to the default calendar of this account. We also use this account’s calendars to check when you’re busy."
              : "We consider these accounts and their calendars when showing your availability to clients."}
          </Box>
          <Box mt={2} color={SUBTEXT_COLOR} maxWidth={["100%", 380]}>
            If no calendars were selected for an account, then the default
            calendar will be used.
          </Box>
        </Box>
        <Box flexBasis={["100%", "50%"]}>
          <Box pl={[0, 4]}>
            {!isPrimary && !hasPrimary && (
              <Box color={SUBTEXT_COLOR} fontSize={14} mb={2}>
                Please connect your primary calendar
              </Box>
            )}
            {calendarAccounts?.map((acc) => (
              <CalendarAccountBlock
                key={acc.id}
                account={acc}
                isPrimary={isPrimary}
                updating={updating}
                makePrimary={makePrimary}
                removeAccount={removeAccount}
                loading={loading}
                responseGoogleHandler={responseGoogleHandler}
                onOpenCalendarSettingModal={onOpenCalendarSettingModal}
              />
            ))}

            {!isPrimary &&
              !addingNew &&
              hasPrimary &&
              (!currentWorkspace.isPersonal ? (
                <div className="bg-red-500/12 round-corners bas color-warning px-4 py-8">
                  <div className="flex items-center justify-center">
                    <div className="mr-2 mt-2 min-w-[20px] h-[20px]">
                      <Icon size={20} intent="warning" icon="warning-sign" />
                    </div>
                    <div>
                      Google and Outlook account can only be added in personal
                      mode
                    </div>
                  </div>
                </div>
              ) : (
                <StyledButton onClick={() => setAddingNew(true)}>
                  Add Account
                </StyledButton>
              ))}
            {addingNew && (
              <Box maxWidth={350} mt={calendarAccounts?.length ? 3 : 0}>
                {currentWorkspace.isPersonal ? (
                  <GrantCalendarAccess
                    responseGoogleHandler={responseGoogleHandler}
                    loading={loading}
                  />
                ) : (
                  <Flex>
                    <Box mr={2} mt={2} minWidth={20} height={20}>
                      <Icon size={20} intent="danger" icon="warning-sign" />
                    </Box>
                    Google account can only be added in personal mode
                  </Flex>
                )}
                {!isPrimary ? <GrantOutlookCalendarAccess /> : null}
              </Box>
            )}
          </Box>
        </Box>
      </Flex>
      {selectedCalendarAccount && (
        <CalendarSettingDialog
          visible={calendarSettingDialogVisible}
          onClose={() => setCalendarSettingDialogVisible(false)}
          selectedCalendarAccount={selectedCalendarAccount}
          setSaved={() => {
            toast.success("Saved");
            onSettingChanged();
          }}
          setErrorMessage={(msg: string) => toast.error(msg)}
        />
      )}
    </>
  );
}

interface CalendarAccountInnerProps {
  primaryCalendarAccount?: CalendarAccount | null;
  otherCalendarAccounts: CalendarAccount[];
  onSettingChanged: () => void;
}

function CalendarAccountInner({
  primaryCalendarAccount,
  otherCalendarAccounts,
  onSettingChanged,
}: CalendarAccountInnerProps) {
  return (
    <>
      <Box mt={4}>
        <CalendarAccountSection
          isPrimary={true}
          hasPrimary={!!primaryCalendarAccount}
          calendarAccounts={
            primaryCalendarAccount ? [primaryCalendarAccount] : []
          }
          onSettingChanged={onSettingChanged}
        />
      </Box>
      <Box className="bts" mt={4} pt={4}>
        <CalendarAccountSection
          isPrimary={false}
          hasPrimary={!!primaryCalendarAccount}
          calendarAccounts={otherCalendarAccounts}
          onSettingChanged={onSettingChanged}
        />
      </Box>
    </>
  );
}

interface CalendarAccountsProps {
  onSettingChanged: () => void;
}

export function CalendarAccounts({ onSettingChanged }: CalendarAccountsProps) {
  const { data, loading } = useCalendarAccountsQuery();
  const showLoader = !data || loading;

  return (
    <Box
      mt={4}
      className={CARD_CLASSES}
      backgroundColor="#FFF"
      justifyContent="space-between"
      alignItems="center"
      py={[3, 3, 3, 24]}
      px={[3, 3, 3, 4]}
    >
      <Box fontSize={20} fontWeight={600}>
        Connected Calendars
      </Box>
      <Box color={SUBTEXT_COLOR}>
        Connect your calendars to ensure you never get double-booked
      </Box>

      {showLoader ? (
        <>
          <Box className={Classes.SKELETON} height={50} mt={4} />
          <Box className={Classes.SKELETON} height={50} mt={3} />
        </>
      ) : (
        <CalendarAccountInner
          primaryCalendarAccount={data.currentUser?.primaryCalendarAccount}
          otherCalendarAccounts={ensureNonNullArray(
            data.currentUser?.otherCalendarAccounts,
          )}
          onSettingChanged={onSettingChanged}
        />
      )}
    </Box>
  );
}
